// This is the React component for Section 4
// It features a "sticky" image column on the left
// and a scrolling text column on the right.

import React, { useLayoutEffect, useRef } from "react";
import "./Section4.css"; // We will create this file next
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Import the image for the runner.
// Make sure to add 'LadyRunning.png' to your 'src/assets/images/' folder
import runnerImage from "../../../assets/images/LadyRunning.png";

gsap.registerPlugin(ScrollTrigger);

// --- Component Text ---
// Storing text in variables makes it easy to edit later
const headlineText =
  "This program is inspired by the incredible passion and dedication of Natalie Thivierge and the Fireweed Runners, in 2021,";

// Split into two paragraphs as you requested
const paragraphText1 =
  "Nat began with some cones, bean bags and a dream to make running fun for girls ages 8-12. Her program has evolved from after school to spring break camp to participation in the Raven 50 and Klondike Road Relays. She has grown the Fireweed girls through the trust of her incredible community collaborators and support team.";
const paragraphText2 =
  "The Fireweed Runners aim to provide a safe and respectful environment, helping participants gain self-confidence and learn positive mental health practices to support them in their daily lives";

// --- React Component ---
function Section4() {
  // --- Refs ---
  const sectionRef = useRef(null); // The main container
  const imageRef = useRef(null); // The image container
  const textContentRef = useRef(null); // The div *inside* the text column

  // --- Animation ---
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // --- Animation for Desktop (sticky layout) ---
      ScrollTrigger.matchMedia({
        "(min-width: 769px)": function () {
          // 1. Animate the image fade-in
          gsap.fromTo(
            imageRef.current,
            { opacity: 0 }, // Start hidden
            {
              opacity: 1, // Fade in
              duration: 2,
              ease: "power2.out",
              scrollTrigger: {
                trigger: sectionRef.current,
                start: "top 60%",
                toggleActions: "play none none none",
                once: true,
              },
            }
          );

          // 2. Animate the text content fade-in
          gsap.fromTo(
            textContentRef.current,
            { opacity: 0, y: 60 }, // Start hidden and down
            {
              opacity: 1, // Fade in
              y: 0, // Move up
              duration: 2,
              ease: "power2.out",
              scrollTrigger: {
                trigger: textContentRef.current,
                start: "top 80%", // Trigger based on the text element itself
                toggleActions: "play none none none",
                once: true,
              },
            }
          );
        },

        // --- Animation for Mobile (stacked layout) ---
        "(max-width: 768px)": function () {
          // Animate *both* elements as a simple waterfall
          gsap.fromTo(
            [imageRef.current, textContentRef.current],
            { opacity: 0, y: 60 },
            {
              opacity: 1,
              y: 0,
              duration: 1,
              stagger: 0.3, // Image first, then text
              ease: "power2.out",
              scrollTrigger: {
                trigger: sectionRef.current,
                start: "top 75%",
                toggleActions: "play none none none",
                once: true,
              },
            }
          );
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // --- JSX (HTML) ---
  return (
    // We attach the main 'sectionRef' here
    <section className="section-4" ref={sectionRef}>
      {/* This is our 2-column layout.
        On Desktop, it's a row. On Mobile, it's a column.
      */}
      <div className="section-4-container">
        
        {/* COLUMN 1: STICKY IMAGE 
          This div is sticky and holds the image.
        */}
        <div className="section-4-image-column" ref={imageRef}>
          <img src={runnerImage} alt="Silhouette of a person running" />
        </div>

        {/* COLUMN 2: SCROLLING TEXT 
          This div scrolls normally.
        */}
        <div className="section-4-text-column">
          {/* We add an inner div so we can add padding
            and animate the content block separately.
          */}
          <div className="section-4-text-content" ref={textContentRef}>
            <h2 className="section-4-headline">{headlineText}</h2>
            
            {/* The two paragraphs, which will be indented with CSS */}
            <p className="section-4-paragraph">{paragraphText1}</p>
            <p className="section-4-paragraph">{paragraphText2}</p>
          </div>
        </div>

      </div>
    </section>
  );
}

export default Section4;